// src/components/Growth/VisitorFollowUp.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  exportToCSV,
  exportToJSON,
  exportToExcel,
  exportToPDF,
  getExportFilename,
} from '../../utils/exportUtils';
import {
  Loader2,
  Users,
  Send,
  TrendingUp,
  Search,
  Plus,
  Trash2,
  UserPlus,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Save,
  RefreshCw,
  Mail,
  Phone,
  Eye,
  Pencil,
  Download,
  BarChart3,
  FileJson,
  FileText,
} from 'lucide-react';
import {
  getVisitors,
  getVisitor,
  createVisitor,
  updateVisitor,
  deleteVisitor,
  sendVisitorMessage,
  convertVisitor,
  resetVisitorJourney,
  getVisitorStats,
  getVisitorSources,
  sendBulkSms,
  exportVisitor,      // <-- single visitor export
  clearApiMessage,
  clearError,
  clearVisitorState,
  selectVisitors,
  selectVisitorPagination,
  selectCurrentVisitor,
  selectVisitorStats,
  selectVisitorSources,
  selectVisitorsLoading,
  selectVisitorsSubmitting,
  selectVisitorsError,
  selectVisitorsApiMessage,
} from '../../store/slices/growth/visitorSlice';

// Source options
const SOURCE_OPTIONS = [
  'Walk-in',
  'Invited by member',
  'Social media',
  'Family member',
  'Outreach program',
  'Website',
  'Referral',
  'Other',
];

const VisitorFollowUp = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [selectedVisitors, setSelectedVisitors] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedVisitor, setSelectedVisitor] = useState(null);

  const [sequenceSteps, setSequenceSteps] = useState([
    {
      day: 'Day 0',
      stage: 'Welcome',
      icon: '👋',
      color: '#4f46e5',
      msg: 'Thank you for visiting today, {FirstName}! We hope you felt at home. Our doors are always open. — Pastor James 🙏',
    },
    {
      day: 'Day 3',
      stage: 'Check-in',
      icon: '❤️',
      color: '#059669',
      msg: 'Hello {FirstName}! It was wonderful having you. We\'d love to see you again this Sunday at 8:30 AM. Any questions? Just reply!',
    },
    {
      day: 'Day 7',
      stage: 'Invite',
      icon: '🌟',
      color: '#d97706',
      msg: 'Hi {FirstName}, we have a special program this weekend! Also, reply JOIN if you\'d like to join a small group.',
    },
    {
      day: 'Day 14',
      stage: 'Connect',
      icon: '🤝',
      color: '#9333ea',
      msg: 'Dear {FirstName}, our membership class starts soon. Reply MEMBER to register. God bless! — Grace Chapel',
    },
  ]);

  // Refs for create modal
  const createFirstNameRef = useRef(null);
  const createLastNameRef = useRef(null);
  const createPhoneRef = useRef(null);
  const createEmailRef = useRef(null);
  const createSourceRef = useRef(null);
  const createVisitDateRef = useRef(null);

  // Refs for edit modal
  const editFirstNameRef = useRef(null);
  const editLastNameRef = useRef(null);
  const editPhoneRef = useRef(null);
  const editEmailRef = useRef(null);
  const editSourceRef = useRef(null);
  const editVisitDateRef = useRef(null);
  const editStatusRef = useRef(null);

  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const visitors = useSelector(selectVisitors) || [];
  const pagination = useSelector(selectVisitorPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  };
  const stats = useSelector(selectVisitorStats) || {
    total: 0,
    converted: 0,
    in_progress: 0,
    no_response: 0,
    conversion_rate: 0,
    sources: [],
  };
  const sources = useSelector(selectVisitorSources) || [];
  const loading = useSelector(selectVisitorsLoading);
  const isSubmittingRedux = useSelector(selectVisitorsSubmitting);
  const apiMessage = useSelector(selectVisitorsApiMessage);
  const error = useSelector(selectVisitorsError);

  // ==================== EFFECTS ====================

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getVisitors({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getVisitorStats()),
          dispatch(getVisitorSources()),
        ]);
      } catch (err) {
        console.error('Error loading visitors:', err);
      }
    };
    loadData();

    return () => {
      dispatch(clearVisitorState());
    };
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm,
      };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;

      dispatch(getVisitors(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, sourceFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    if (visitors.length > 0 && !selectedVisitor) {
      setSelectedVisitor(visitors[0]);
    }
  }, [visitors]);

  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;
  const currentPageNum = pagination.current_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) setCurrentPage(currentPageNum - 1);
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) setCurrentPage(currentPageNum + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== VISITOR CRUD ====================

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const map = {
      'In Progress': { class: 'badge-blue', label: 'In Progress' },
      'Converted': { class: 'badge-green', label: 'Converted' },
      'No Response': { class: 'badge-amber', label: 'No Response' },
    };
    const s = map[status] || map['In Progress'];
    return <span className={`badge ${s.class}`}>{s.label}</span>;
  };

  // ---------- CREATE ----------
  const openCreateVisitor = () => {
    const today = new Date().toISOString().split('T')[0];
    setTimeout(() => {
      if (createFirstNameRef.current) createFirstNameRef.current.value = '';
      if (createLastNameRef.current) createLastNameRef.current.value = '';
      if (createPhoneRef.current) createPhoneRef.current.value = '';
      if (createEmailRef.current) createEmailRef.current.value = '';
      if (createSourceRef.current) createSourceRef.current.value = SOURCE_OPTIONS[0];
      if (createVisitDateRef.current) {
        createVisitDateRef.current.value = today;
      }
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <UserPlus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Add New Visitor
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={createFirstNameRef} placeholder="John" />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={createLastNameRef} placeholder="Doe" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={createPhoneRef} placeholder="+233241234567" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" ref={createEmailRef} placeholder="john@example.com" type="email" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Source</label>
              <select className="form-select" ref={createSourceRef} defaultValue={SOURCE_OPTIONS[0]}>
                {SOURCE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Visit Date</label>
              <input className="form-input" type="date" ref={createVisitDateRef} defaultValue={today} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              const firstName = createFirstNameRef.current?.value?.trim() || '';
              const lastName = createLastNameRef.current?.value?.trim() || '';
              const phone = createPhoneRef.current?.value?.trim() || '';
              const email = createEmailRef.current?.value?.trim() || '';
              const source = createSourceRef.current?.value || SOURCE_OPTIONS[0];
              const visitDate = createVisitDateRef.current?.value || today;

              if (!firstName || !lastName || !phone) {
                showToastMessage('Please fill in all required fields', 'error');
                return;
              }

              try {
                const result = await dispatch(createVisitor({
                  first_name: firstName,
                  last_name: lastName,
                  phone,
                  email,
                  source,
                  visit_date: visitDate,
                  status: 'In Progress',
                })).unwrap();

                if (result.success) {
                  closeModal();
                  await dispatch(getVisitors({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter, source: sourceFilter }));
                  await dispatch(getVisitorStats());
                  setSelectedVisitor(result.visitor);
                  showToastMessage(`✅ ${firstName} ${lastName} added as a visitor!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to add visitor', 'error');
              }
            }}
          >
            {isSubmittingRedux ? 'Adding...' : 'Add Visitor'}
          </button>
        </div>
      </div>
    );
  };

  // ---------- EDIT ----------
  const openEditVisitor = (visitor) => {
    const today = new Date().toISOString().split('T')[0];
    setTimeout(() => {
      if (editFirstNameRef.current) editFirstNameRef.current.value = visitor.first_name || '';
      if (editLastNameRef.current) editLastNameRef.current.value = visitor.last_name || '';
      if (editPhoneRef.current) editPhoneRef.current.value = visitor.phone || '';
      if (editEmailRef.current) editEmailRef.current.value = visitor.email || '';
      if (editSourceRef.current) editSourceRef.current.value = visitor.source || SOURCE_OPTIONS[0];
      if (editVisitDateRef.current) {
        editVisitDateRef.current.value = visitor.visit_date || today;
      }
      if (editStatusRef.current) editStatusRef.current.value = visitor.status || 'In Progress';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Edit Visitor
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={editFirstNameRef} placeholder="John" />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={editLastNameRef} placeholder="Doe" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={editPhoneRef} placeholder="+233241234567" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" ref={editEmailRef} placeholder="john@example.com" type="email" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Source</label>
              <select className="form-select" ref={editSourceRef}>
                {SOURCE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Visit Date</label>
              <input className="form-input" type="date" ref={editVisitDateRef} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" ref={editStatusRef}>
              <option value="In Progress">In Progress</option>
              <option value="Converted">Converted</option>
              <option value="No Response">No Response</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              const firstName = editFirstNameRef.current?.value?.trim() || '';
              const lastName = editLastNameRef.current?.value?.trim() || '';
              const phone = editPhoneRef.current?.value?.trim() || '';
              const email = editEmailRef.current?.value?.trim() || '';
              const source = editSourceRef.current?.value || SOURCE_OPTIONS[0];
              const visitDate = editVisitDateRef.current?.value || today;
              const status = editStatusRef.current?.value || 'In Progress';

              if (!firstName || !lastName || !phone) {
                showToastMessage('Please fill in all required fields', 'error');
                return;
              }

              try {
                const result = await dispatch(updateVisitor({
                  id: visitor.id,
                  data: {
                    first_name: firstName,
                    last_name: lastName,
                    phone,
                    email,
                    source,
                    visit_date: visitDate,
                    status,
                  }
                })).unwrap();

                if (result.success) {
                  closeModal();
                  await dispatch(getVisitors({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter, source: sourceFilter }));
                  await dispatch(getVisitorStats());
                  if (selectedVisitor?.id === visitor.id) {
                    setSelectedVisitor(result.visitor);
                  }
                  showToastMessage(`✅ ${firstName} ${lastName} updated successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update visitor', 'error');
              }
            }}
          >
            {isSubmittingRedux ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== VIEW JOURNEY (row click) ====================
  const handleViewJourney = (visitor) => {
    setSelectedVisitor(visitor);
    dispatch(getVisitor(visitor.id))
      .unwrap()
      .then((result) => {
        if (result.visitor) {
          setSelectedVisitor(result.visitor);
        }
      })
      .catch(() => {});
  };

  // ==================== VIEW JOURNEY MODAL (eye icon) ====================
  const openJourneyModal = (visitor) => {
    const steps = sequenceSteps.map((step) => ({
      ...step,
      personalizedMsg: step.msg.replace(/{FirstName}/g, visitor.first_name || 'Visitor'),
    }));

    const currentStage = visitor.stage || 0;
    const totalStages = visitor.total_stages || sequenceSteps.length;
    const isConverted = visitor.status === 'Converted';

    openModal(
      <div>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="member-row-avatar" style={{ background: '#4f46e5', width: '40px', height: '40px', fontSize: '16px' }}>
              {visitor.first_name?.[0]}{visitor.last_name?.[0] || ''}
            </div>
            <div>
              <div className="modal-title">{visitor.first_name} {visitor.last_name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                {visitor.phone} · {visitor.group || 'No group'} · Stage {currentStage}/{totalStages}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {[
                ['Status', getStatusBadge(visitor.status)],
                ['Visit Date', formatDate(visitor.visit_date)],
                ['Source', visitor.source || '—'],
                ['Last Contact', visitor.last_contact ? formatDate(visitor.last_contact) : '—'],
                ['Stage', `${currentStage}/${totalStages}`],
                ['Converted', isConverted ? '✅ Yes' : '❌ No'],
              ].map(([label, value]) => (
                <div key={label} style={{ padding: '8px 12px', background: 'var(--cream)', borderRadius: '8px', minWidth: '120px' }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-light)', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginBottom: '8px' }}>
              Follow-Up Journey
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {steps.map((step, index) => {
                const isCompleted = index < currentStage;
                const isCurrent = index === currentStage && !isConverted;
                const isPending = index > currentStage || isConverted;

                const borderColor = isCompleted ? 'var(--green)' : isCurrent ? 'var(--gold)' : 'var(--border)';
                const bgColor = isCompleted ? 'var(--green-bg)' : isCurrent ? 'var(--cream)' : 'var(--cream)';

                return (
                  <div
                    key={step.day}
                    style={{
                      border: `2px solid ${borderColor}`,
                      borderRadius: '8px',
                      padding: '12px',
                      background: bgColor,
                      opacity: isPending && !isConverted ? 0.6 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '18px' }}>{step.icon}</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)' }}>
                        {step.day} – {step.stage}
                        {isCompleted && ' ✅'}
                        {isCurrent && ' ⏳'}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-mid)', lineHeight: '1.5' }}>
                      {step.personalizedMsg}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button
            className="btn btn-gold btn-sm"
            onClick={() => {
              closeModal();
              handleSendSms(visitor);
            }}
          >
            <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            Send SMS
          </button>
        </div>
      </div>
    );
  };

  // ---------- DELETE ----------
  const handleDeleteVisitor = (visitor) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Delete Visitor
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{visitor.first_name} {visitor.last_name}</strong>?
          </p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            This will permanently remove the visitor from the system.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-danger btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              try {
                const result = await dispatch(deleteVisitor(visitor.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getVisitors({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter, source: sourceFilter }));
                  await dispatch(getVisitorStats());
                  if (selectedVisitor?.id === visitor.id) setSelectedVisitor(null);
                  showToastMessage(`🗑️ ${visitor.first_name} ${visitor.last_name} deleted`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete visitor', 'error');
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  // ---------- SEND SINGLE SMS ----------
  const handleSendSms = (visitor) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Send SMS to {visitor.first_name}
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send SMS to <strong>{visitor.first_name} {visitor.last_name}</strong> ({visitor.phone})
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea
              className="form-textarea"
              rows="4"
              id={`sms-message-${visitor.id}`}
              placeholder={`Hello ${visitor.first_name}, this is a follow-up from our church...`}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              const message = document.getElementById(`sms-message-${visitor.id}`)?.value?.trim() || '';
              if (!message) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              try {
                const result = await dispatch(sendVisitorMessage(visitor.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getVisitor(visitor.id));
                  showToastMessage(`📨 SMS sent to ${visitor.first_name}!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send SMS', 'error');
              }
            }}
          >
            Send SMS
          </button>
        </div>
      </div>
    );
  };

  // ---------- CONVERT ----------
  const handleConvertVisitor = (visitor) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <UserPlus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Convert to Member
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to convert <strong>{visitor.first_name} {visitor.last_name}</strong> to a full member?
          </p>
          <div style={{ padding: '12px', background: 'var(--green-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--green)' }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            The visitor will be added as a member and removed from the visitor list.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux}
            onClick={async () => {
              try {
                const result = await dispatch(convertVisitor(visitor.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getVisitors({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter, source: sourceFilter }));
                  await dispatch(getVisitorStats());
                  if (selectedVisitor?.id === visitor.id) setSelectedVisitor(null);
                  showToastMessage(`✅ ${visitor.first_name} ${visitor.last_name} converted to member!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to convert visitor', 'error');
              }
            }}
          >
            Convert
          </button>
        </div>
      </div>
    );
  };

  // ---------- RESET JOURNEY ----------
  const handleResetJourney = async (visitor) => {
    try {
      const result = await dispatch(resetVisitorJourney(visitor.id)).unwrap();
      if (result.success) {
        await dispatch(getVisitors({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter, source: sourceFilter }));
        await dispatch(getVisitorStats());
        const updated = await dispatch(getVisitor(visitor.id)).unwrap();
        setSelectedVisitor(updated.visitor || null);
        showToastMessage(`🔄 Journey reset for ${visitor.first_name} ${visitor.last_name}`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to reset journey', 'error');
    }
  };

  // ==================== EXPORT FUNCTIONS ====================

  // Core export – performs the actual download
  const performExport = (data, title, filename, format) => {
    if (!Array.isArray(data) || data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    const fullFilename = getExportFilename(filename);

    switch (format.toLowerCase()) {
      case 'csv':
        exportToCSV(data, fullFilename);
        break;
      case 'json':
        exportToJSON(data, fullFilename);
        break;
      case 'excel':
        exportToExcel(data, fullFilename);
        break;
      case 'pdf':
        exportToPDF(data, title, fullFilename);
        break;
      default:
        showToastMessage('Unsupported export format', 'error');
        return;
    }

    showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
    closeModal();
  };

  // Opens the export modal with data already prepared
  const openExportModalWithData = (data, title, filename, exportType, visitor) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
            {exportType === 'single' && visitor && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {visitor.first_name} {visitor.last_name}
              </span>
            )}
            {exportType === 'summary' && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · Summary of {visitors.length} visitors
              </span>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => performExport(data, title, filename, format)}
                  disabled={isSubmitting}
                  style={{
                    padding: '8px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <FileJson size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Main export handler – prepares data and opens modal
  const handleExport = async (exportType, visitor = null) => {
    try {
      setIsSubmitting(true);

      let data = [];
      let title = 'Visitors Report';
      let filename = 'visitors-report';

      if (exportType === 'single' && visitor) {
        // Fetch single visitor via API
        const result = await dispatch(exportVisitor(visitor.id)).unwrap();
        if (result.success && result.data) {
          let exportData = result.data;
          // If the response is an object, wrap it
          if (!Array.isArray(exportData)) {
            exportData = [exportData];
          }
          data = exportData;
          title = `Visitor: ${visitor.first_name} ${visitor.last_name}`;
          filename = `visitor-${visitor.id}-${visitor.first_name}`;
        } else {
          showToastMessage('Failed to fetch visitor data', 'error');
          return;
        }
      } else if (exportType === 'summary') {
        // Summary – use local visitors state
        data = visitors.map(v => ({
          'Name': `${v.first_name} ${v.last_name}`,
          'Phone': v.phone,
          'Email': v.email || '—',
          'Source': v.source || '—',
          'Visit Date': formatDate(v.visit_date),
          'Status': v.status || 'In Progress',
          'Stage': `${v.stage || 0}/${v.total_stages || sequenceSteps.length}`,
          'Conversion Status': v.status === 'Converted' ? '✅ Converted' : '—',
        }));
        title = 'Visitors Summary Report';
        filename = 'visitors-summary';
      } else {
        // All visitors – detailed
        data = visitors.map(v => ({
          'Name': `${v.first_name} ${v.last_name}`,
          'Phone': v.phone,
          'Email': v.email || '—',
          'Source': v.source || '—',
          'Visit Date': formatDate(v.visit_date),
          'Status': v.status || 'In Progress',
          'Stage': `${v.stage || 0}/${v.total_stages || sequenceSteps.length}`,
          'Last Contact': formatDate(v.last_contact),
          'Group': v.group || '—',
          'Notes': v.notes || '—',
          'Created At': formatDate(v.created_at),
        }));
        title = 'All Visitors Report';
        filename = 'all-visitors';
      }

      if (data.length === 0) {
        showToastMessage('No data to export', 'error');
        return;
      }

      openExportModalWithData(data, title, filename, exportType, visitor);
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shortcut handlers
  const handleExportAll = () => {
    if (visitors.length === 0) {
      showToastMessage('No visitors to export', 'error');
      return;
    }
    handleExport('all');
  };

  const handleExportSingle = (visitor) => {
    handleExport('single', visitor);
  };

  const handleExportSummary = () => {
    if (visitors.length === 0) {
      showToastMessage('No visitors to export', 'error');
      return;
    }
    handleExport('summary');
  };

  // ==================== BULK ACTIONS ====================

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedVisitors(visitors.map(v => v.id));
      setSelectAll(true);
    } else {
      setSelectedVisitors([]);
      setSelectAll(false);
    }
  };

  const handleSelectVisitor = (id) => {
    if (selectedVisitors.includes(id)) {
      setSelectedVisitors(selectedVisitors.filter(visId => visId !== id));
    } else {
      setSelectedVisitors([...selectedVisitors, id]);
    }
  };

  // ---------- BULK SMS ----------
  const handleBulkSendSms = () => {
    if (selectedVisitors.length === 0) {
      showToastMessage('Please select at least one visitor', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Send Bulk SMS
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send SMS to <strong>{selectedVisitors.length}</strong> selected visitor(s).
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea
              className="form-textarea"
              rows="4"
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              placeholder="Hello {FirstName}, this is a message from Grace Chapel..."
            />
            <div className="form-hint">Use {'{FirstName}'} for personalisation</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux || !broadcastMessage.trim()}
            onClick={async () => {
              if (!broadcastMessage.trim()) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              try {
                const result = await dispatch(sendBulkSms({
                  visitor_ids: selectedVisitors,
                  message: broadcastMessage.trim(),
                  sender_id: 'CHURCH',
                })).unwrap();
                if (result.success) {
                  closeModal();
                  setSelectedVisitors([]);
                  setSelectAll(false);
                  setBroadcastMessage('');
                  showToastMessage(`📨 Bulk SMS sent to ${selectedVisitors.length} visitors!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send bulk SMS', 'error');
              }
            }}
          >
            {isSubmittingRedux ? 'Sending...' : 'Send to Selected'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== EDIT SEQUENCE ====================

  const openEditSequence = () => {
    const editSteps = sequenceSteps.map((step) => ({ ...step }));

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">
            <Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />
            Edit Follow-Up Sequence
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Customise the messages sent at each stage of the visitor follow‑up journey.
            Use <code>{'{FirstName}'}</code> to personalise each message.
          </p>
          {editSteps.map((step, index) => (
            <div
              key={index}
              style={{
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                borderLeft: `4px solid ${step.color}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '18px' }}>{step.icon}</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                  {step.day} – {step.stage}
                </span>
              </div>
              <textarea
                className="form-textarea"
                rows="2"
                value={step.msg}
                onChange={(e) => {
                  editSteps[index].msg = e.target.value;
                }}
                style={{ fontSize: '12px' }}
              />
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            onClick={() => {
              setSequenceSteps(editSteps);
              closeModal();
              showToastMessage('✅ Sequence updated successfully!', 'success');
            }}
          >
            <Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            Save Sequence
          </button>
        </div>
      </div>
    );
  };

  // ==================== DYNAMIC FOLLOW-UP SEQUENCE (below table) ====================

  const renderSequence = () => {
    if (!selectedVisitor) {
      return (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-light)' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>👤</div>
          <div>Select a visitor from the table to view their journey.</div>
        </div>
      );
    }

    const visitor = selectedVisitor;
    const currentStage = visitor.stage || 0;
    const totalStages = visitor.total_stages || sequenceSteps.length;

    const steps = sequenceSteps.map((step, index) => {
      const isCompleted = index < currentStage;
      const isCurrent = index === currentStage && visitor.status !== 'Converted';
      const isPending = index > currentStage || visitor.status === 'Converted';

      return {
        ...step,
        isCompleted,
        isCurrent,
        isPending,
        personalizedMsg: step.msg.replace(/{FirstName}/g, visitor.first_name || 'Visitor'),
      };
    });

    const isConverted = visitor.status === 'Converted';

    return (
      <div>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: '600', color: 'var(--navy)' }}>
              {visitor.first_name} {visitor.last_name}
            </span>
            <span className="badge badge-navy" style={{ fontSize: '10px' }}>
              Stage {currentStage} of {totalStages}
            </span>
            {isConverted && (
              <span className="badge badge-green" style={{ fontSize: '10px' }}>
                ✅ Converted
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {isConverted
              ? 'Journey completed — visitor converted to member.'
              : currentStage === totalStages
              ? 'All steps completed — ready for conversion.'
              : `${totalStages - currentStage} steps remaining.`}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {steps.map((step, index) => {
            const borderColor = step.isCompleted ? 'var(--green)' : step.isCurrent ? 'var(--gold)' : 'var(--border)';
            const bgColor = step.isCompleted ? 'var(--green-bg)' : step.isCurrent ? 'var(--cream)' : 'var(--cream)';

            return (
              <div
                key={step.day}
                style={{
                  border: `2px solid ${borderColor}`,
                  borderRadius: '10px',
                  padding: '14px',
                  background: bgColor,
                  opacity: step.isPending && !isConverted ? 0.6 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: step.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                    }}
                  >
                    {step.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)' }}>
                      {step.day}
                      {step.isCompleted && ' ✅'}
                      {step.isCurrent && ' ⏳'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{step.stage}</div>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-mid)', lineHeight: '1.6' }}>
                  {step.personalizedMsg}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
            <Users size={20} style={{ verticalAlign: '-4px', marginRight: '6px' }} />
            Visitor Follow-Up
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
            Track every first-time visitor and manage their SMS follow-up journey.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedVisitors.length > 0 && (
            <button className="btn btn-gold btn-sm" onClick={handleBulkSendSms} disabled={isSubmittingRedux}>
              <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
              Send Bulk ({selectedVisitors.length})
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportSummary}
            title="Export summary of all visitors"
            disabled={isSubmitting}
          >
            <BarChart3 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all visitors"
            disabled={isSubmitting}
          >
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export All
          </button>
          <button className="btn btn-gold btn-sm" onClick={openCreateVisitor} disabled={isSubmittingRedux}>
            <Plus size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            Add Visitor
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Visitors</div>
          <div className="stat-value">{stats.total || 0}</div>
          <div className="stat-change up">↑ all time</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Converted</div>
          <div className="stat-value">{stats.converted || 0}</div>
          <div className="stat-change up">↑ members</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">In Progress</div>
          <div className="stat-value">{stats.in_progress || 0}</div>
          <div className="stat-change up">→ active</div>
          <div className="stat-icon"><Clock size={20} /></div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label">Conversion Rate</div>
          <div className="stat-value">{stats.conversion_rate || 0}%</div>
          <div className="stat-change up">↑ {stats.conversion_rate > 50 ? 'good' : 'needs improvement'}</div>
          <div className="stat-icon"><TrendingUp size={20} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input
              type="text"
              placeholder="Search visitors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="In Progress">In Progress</option>
            <option value="Converted">Converted</option>
            <option value="No Response">No Response</option>
          </select>
          <select
            className="form-select"
            style={{ width: '140px' }}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="">All Sources</option>
            {sources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
          <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-light)' }}>
            {visitors.length} visitors found
          </div>
        </div>
      </div>

      {/* Visitors Table */}
      {loading && visitors.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading visitors...</div>
        </div>
      ) : visitors.length > 0 ? (
        <>
          <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        style={{ accentColor: 'var(--navy)' }}
                        checked={selectAll && visitors.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Visitor</th>
                    <th>Visit Date</th>
                    <th>Source</th>
                    <th>Journey</th>
                    <th>Last Contact</th>
                    <th>Status</th>
                    <th style={{ minWidth: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map(visitor => {
                    const totalStages = visitor.total_stages || sequenceSteps.length;
                    const stage = visitor.stage || 0;
                    const colors = ['#4f46e5', '#059669', '#d97706', '#9333ea', '#e11d48'];
                    const color = colors[visitor.id % colors.length];

                    return (
                      <tr
                        key={visitor.id}
                        style={{
                          cursor: 'pointer',
                          background: selectedVisitor?.id === visitor.id ? 'var(--cream)' : 'transparent',
                        }}
                        onClick={() => handleViewJourney(visitor)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            style={{ accentColor: 'var(--navy)' }}
                            checked={selectedVisitors.includes(visitor.id)}
                            onChange={() => handleSelectVisitor(visitor.id)}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="member-row-avatar" style={{ background: color, width: '32px', height: '32px', fontSize: '12px' }}>
                              {visitor.first_name?.[0]}{visitor.last_name?.[0] || ''}
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: 'var(--navy)' }}>
                                {visitor.first_name} {visitor.last_name}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                                <Phone size={11} style={{ verticalAlign: '-2px' }} /> {visitor.phone}
                              </div>
                              {visitor.email && (
                                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                                  <Mail size={11} style={{ verticalAlign: '-2px' }} /> {visitor.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-mid)' }}>
                          {formatDate(visitor.visit_date)}
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-mid)' }}>
                          {visitor.source || '—'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {Array.from({ length: totalStages }).map((_, i) => (
                              <div
                                key={i}
                                style={{
                                  width: '18px',
                                  height: '6px',
                                  borderRadius: '3px',
                                  background: i < stage ? color : 'var(--border)',
                                }}
                              />
                            ))}
                            <span style={{ fontSize: '11px', color: 'var(--text-light)', marginLeft: '4px' }}>
                              {stage}/{totalStages}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-mid)' }}>
                          {visitor.last_contact ? formatDate(visitor.last_contact) : '—'}
                        </td>
                        <td>{getStatusBadge(visitor.status)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => openJourneyModal(visitor)}
                              title="View detailed journey"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => openEditVisitor(visitor)}
                              title="Edit visitor"
                              disabled={isSubmittingRedux}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleSendSms(visitor)}
                              title="Send SMS"
                              disabled={isSubmittingRedux}
                            >
                              <Send size={14} />
                            </button>
                            {visitor.status !== 'Converted' && (
                              <button
                                className="btn btn-success btn-sm btn-icon"
                                onClick={() => handleConvertVisitor(visitor)}
                                title="Convert to member"
                                disabled={isSubmittingRedux}
                                style={{ background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid #a7d7be' }}
                              >
                                <UserPlus size={14} />
                              </button>
                            )}
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleResetJourney(visitor)}
                              title="Reset journey"
                              disabled={isSubmittingRedux}
                            >
                              <RefreshCw size={14} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleExportSingle(visitor)}
                              title="Export visitor data"
                              disabled={isSubmitting}
                            >
                              <Download size={14} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleDeleteVisitor(visitor)}
                              title="Delete visitor"
                              style={{ color: 'var(--red)' }}
                              disabled={isSubmittingRedux}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                padding: '14px 20px',
                borderTop: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                  Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to{' '}
                  <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of{' '}
                  <strong>{totalItems}</strong> visitors
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                    <select
                      className="form-select"
                      style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      disabled={loading}
                    >
                      <option value={3}>3</option>
                      <option value={6}>6</option>
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={goToPreviousPage}
                      disabled={currentPageNum === 1 || loading}
                      style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}
                    >
                      ← Prev
                    </button>

                    {getPageNumbers().map(page => (
                      <button
                        key={page}
                        className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => goToPage(page)}
                        disabled={loading}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={goToNextPage}
                      disabled={currentPageNum === totalPages || loading}
                      style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dynamic Follow-Up Sequence (below table) */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Follow-Up Sequence</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <span className="badge badge-green">Auto-Running</span>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={openEditSequence}
                  title="Edit sequence messages"
                >
                  <Pencil size={14} />
                </button>
              </div>
            </div>
            {renderSequence()}
            <button
              className="btn btn-gold btn-sm"
              style={{ marginTop: '14px' }}
              onClick={openEditSequence}
            >
              <Pencil size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
              Edit Sequence
            </button>
          </div>
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon"><Users size={48} strokeWidth={1.5} /></div>
          <div className="empty-title">No visitors found</div>
          <div className="empty-text">Try adjusting your search or filters, or add a new visitor.</div>
          <button className="btn btn-gold btn-sm" onClick={openCreateVisitor}>
            <Plus size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Add Visitor
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .stat-card {
          position: relative;
          padding: 16px 20px;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: default;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }
        .stat-card .stat-label {
          font-size: 12px;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .stat-card .stat-value {
          font-size: 28px;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
          color: var(--navy);
          margin: 4px 0 2px;
        }
        .stat-card .stat-change {
          font-size: 12px;
          font-weight: 600;
        }
        .stat-card .stat-change.up { color: var(--green); }
        .stat-card .stat-change.down { color: var(--red); }
        .stat-card .stat-icon {
          position: absolute;
          top: 16px;
          right: 16px;
          opacity: 0.2;
          color: var(--navy);
        }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.blue .stat-icon { color: var(--blue); }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .btn-gold {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-gold:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }
        .btn-success {
          background: var(--green) !important;
          color: white !important;
          border: none !important;
        }
        .btn-success:hover {
          background: var(--green-dark) !important;
          color: white !important;
        }
        .btn-danger {
          background: var(--red) !important;
          color: white !important;
          border: none !important;
        }
        .btn-danger:hover {
          background: var(--red-dark) !important;
          color: white !important;
        }
        .btn-ghost {
          background: transparent !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-ghost:hover {
          background: var(--cream) !important;
          color: var(--text) !important;
        }
        .btn-primary {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }
        .btn-sm {
          padding: 4px 10px !important;
          font-size: 12px !important;
        }
        .btn-icon {
          padding: 4px 6px !important;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .empty-state {
          text-align: center;
          padding: 40px 0;
        }
        .empty-icon {
          font-size: 48px;
          margin-bottom: 12px;
          color: var(--text-light);
        }
        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 4px;
        }
        .empty-text {
          font-size: 14px;
          color: var(--text-light);
          margin-bottom: 16px;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          background: var(--cream);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .table-wrap table {
            min-width: 700px;
          }
        }
      `}</style>
    </>
  );
};

export default VisitorFollowUp;